import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Minus, Plus } from "lucide-react";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCheckout } from "@/hooks/useCheckout";
import { toast } from "sonner";

const QtyPicker = ({ qty, setQty }) => (
  <div className="flex items-center rounded-full border border-purple-300/30">
    <button data-testid="product-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-amber-200" aria-label="Less"><Minus className="w-4 h-4" /></button>
    <span data-testid="product-qty-value" className="w-8 text-center font-mono">{qty}</span>
    <button data-testid="product-qty-increase" onClick={() => setQty(Math.min(20, qty + 1))} className="p-3 hover:text-amber-200" aria-label="More"><Plus className="w-4 h-4" /></button>
  </div>
);

export const ProductModal = ({ product, onClose }) => {
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();
  const { checkout, busy } = useCheckout();
  if (!product) return null;

  const addToCart = () => {
    add(product, qty);
    toast(`${product.name} × ${qty} added`);
    onClose();
    setOpen(true);
  };
  const buyNow = () => checkout([{ product_id: product.id, quantity: qty }]);

  return (
    <Dialog open={!!product} onOpenChange={(o) => { if (!o) { setQty(1); onClose(); } }}>
      <DialogContent data-testid="product-modal" className="max-w-4xl p-0 overflow-hidden bg-[#170a24] border-purple-400/20 max-h-[92vh] overflow-y-auto">
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto md:min-h-[520px] overflow-hidden" style={{ clipPath: "inset(0 round 0 0 160px 0)" }}>
            <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="p-7 md:p-10 flex flex-col">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-purple-300">{product.intention} · {product.stone}</span>
            <DialogTitle className="font-display font-normal text-4xl md:text-5xl mt-4 leading-none">{product.name}</DialogTitle>
            <DialogDescription className="mt-5 text-purple-100/85 leading-relaxed text-base">{product.description}</DialogDescription>
            <p className="mt-6 font-mono text-2xl text-amber-200" data-testid="product-modal-price">{money(product.price)}</p>
            <div className="mt-auto pt-8 space-y-4">
              <QtyPicker qty={qty} setQty={setQty} />
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="product-add-to-cart-btn" onClick={addToCart}
                  className="rounded-full border border-purple-300/40 py-3.5 hover:border-amber-200 hover:text-amber-200 transition-colors">
                  Put in cart
                </button>
                <button data-testid="product-buy-now-btn" onClick={buyNow} disabled={busy}
                  className="rounded-full bg-purple-200 text-[#0e0616] py-3.5 font-medium hover:bg-amber-200 transition-colors disabled:opacity-60">
                  {busy ? "Opening…" : "Buy now"}
                </button>
              </div>
              <p className="text-xs text-purple-200/80">Secure checkout by Stripe. Sign in with Google to buy.</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
